import { formatSuccessFeeRate } from '@/libs/utils/format-success-fee-rate';
import { Flex, Grid, Stack, Text } from '@chakra-ui/react';
import { Check, Lock } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { PlanCollabFeeLines } from '../plan-fees/PlanCollabFeeLines';
import { PLAN_CHOICE_FEATURES } from './plan-choice-features';
import type { PlanChoice } from './plan-choice-offer';

/** Every line of every plan, fees included, opened from "Compare all plans". */
export const PlanComparison = ({
  id,
  choices,
  currencyCode,
}: {
  id: string;
  choices: PlanChoice[];
  /** The organization's currency, which writes the collab minimum when a plan has no price. */
  currencyCode?: string | null;
}) => {
  const { t } = useTranslation();
  const locale = t('common.locale');

  return (
    <Grid
      id={id}
      data-testid="plan-comparison"
      templateColumns={{ base: '1fr', md: 'repeat(3, minmax(0, 1fr))' }}
      gap="12px"
      mt="12px"
    >
      {choices.map((choice) => (
        <Stack
          key={choice.id}
          data-testid={`plan-comparison-${choice.id}`}
          gap="8px"
          px="18px"
          py="16px"
          borderRadius="16px"
          bg="background"
        >
          <Text fontFamily="heading" fontSize="15px" fontWeight="600">
            {t(`pricing.plan.${choice.id}.name`)}
          </Text>
          <Stack gap="2px" fontSize="12.5px" color="inkMuted">
            <Text>
              {t(
                choice.id === 'free'
                  ? 'pricing.plan.affiliateFee'
                  : 'pricing.plan.affiliateFeeOnTop',
                {
                  rate: formatSuccessFeeRate(choice.successFeeBasisPoints, locale),
                }
              )}
            </Text>
            <PlanCollabFeeLines
              collabFee={choice.collabFee}
              giftingFeeBasisPoints={choice.giftingFeeBasisPoints}
              currencyCode={choice.price?.currencyCode ?? currencyCode ?? 'EUR'}
            />
          </Stack>
          <Stack as="ul" gap="7px" listStyleType="none" m={0} p={0}>
            {PLAN_CHOICE_FEATURES[choice.id].map(({ key, values, locked }) => {
              const Icon = locked ? Lock : Check;
              return (
                <Flex
                  as="li"
                  key={key}
                  align="flex-start"
                  gap="8px"
                  fontSize="12.5px"
                  lineHeight="1.4"
                  color={locked ? 'inkSubtle' : 'navInactive'}
                >
                  <Icon width={14} height={14} style={{ flex: 'none', marginTop: 1 }} aria-hidden />
                  {t(key, values)}
                </Flex>
              );
            })}
          </Stack>
        </Stack>
      ))}
    </Grid>
  );
};
